import BedSlotSelector from '@/components/drying-lots/BedSlotSelector';
import FormField from '@/components/FormField';
import { Button } from '@/components/shadcn-ui/button';
import { Card } from '@/components/shadcn-ui/card';
import Loader from '@/components/sharedComponents/Loader';
import { startDrying } from '@/network/api.service';
import { startDryingLotSchema } from '@/schema/drying-lot-schema';
import { useLotStore } from '@/stores/useLot.store';
import { useMasterStore } from '@/stores/useMaster.store';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const DryingStartPage = () => {
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { lot_id } = useParams();
  const fetchLot = useLotStore(state => state.fetchLot);
  const lot = useLotStore(state => state.lot);
  const dryingBeds = useMasterStore(state => state.dryingBeds);
  const fetchDryingBeds = useMasterStore(state => state.fetchDryingBeds);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(startDryingLotSchema),
    defaultValues: {
      drying_start_time: new Date(),
      drying_beds: [],
    },
  });

  const startOfDrying = watch('drying_start_time');

  const onSubmit = async data => {
    try {
      setLoading(true);

      const convertDateToLocalTimeUTC = date => {
        if (!date || !(date instanceof Date)) return date;

        // Get local time components
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const milliseconds = date.getMilliseconds();

        // Create a new Date using UTC methods but with local time values
        // This makes the time appear as-is in UTC
        return new Date(Date.UTC(year, month, day, hours, minutes, seconds, milliseconds));
      };

      const processedData = {
        ...data,
        drying_start_time: convertDateToLocalTimeUTC(data.drying_start_time),
        // Also convert drying_start_time in drying_beds array if it exists
        drying_beds: data.drying_beds?.map(bed => ({
          ...bed,
          drying_start_time: convertDateToLocalTimeUTC(bed.drying_start_time),
        })),
      };

      const response = await startDrying(lot_id, processedData);

      if (response?.data?.success) {
        toast.success('Drying started successfully');
        navigate(`/drying-lots/in-progress/${lot_id}`, { replace: true });
      }
      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLotData = async () => {
      setPageLoading(true);
      await fetchDryingBeds();
      await fetchLot(lot_id, false);
      setPageLoading(false);
    };

    fetchLotData();
  }, [lot_id]);

  useEffect(() => {
    if (errors.drying_beds) {
      console.error('Drying beds validation error:', errors.drying_beds);
    }
  }, [errors.drying_beds]);

  if (pageLoading) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      {/* Lot Information */}
      <Card className="flex flex-col gap-1 p-4">
        <div className="mb-1 text-base font-semibold">Lot Information</div>
        <div className="text-sm">
          <span className="font-semibold">Lot:</span> {lot?.lotname}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Estate:</span> {lot?.estate?.name}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Date of harvest:</span>{' '}
          {new Date(lot?.date_of_harvest).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </Card>

      {/* Drying Start Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card className="p-4">
          <div className="text-base font-semibold">Drying Start</div>
          <div className="mt-3.5 flex flex-col gap-3">
            <FormField
              datetime
              label="Start of Drying"
              name="drying_start_time"
              required
              control={control}
              disabled={loading}
              errors={errors}
            />
            <FormField
              label="Input weight of coffee (kg)"
              name="drying_start_weight_kg"
              required
              placeholder="Enter weight"
              type="number"
              step="0.01"
              register={register}
              disabled={loading}
              errors={errors}
            />
          </div>
        </Card>

        <Card className="space-y-4 p-4">
          <div className="text-base font-semibold">Assign Beds</div>
          <BedSlotSelector dryingBeds={dryingBeds} control={control} startOfDrying={startOfDrying} lot={lot} />
          {errors.drying_beds && <div className="text-xs text-red-500">{errors.drying_beds.message}</div>}
          <Button type="submit" className="w-full">
            Start
          </Button>
        </Card>
      </form>
    </div>
  );
};

export default DryingStartPage;
